import React, { FC, useState, useRef, useEffect, CSSProperties } from 'react';
import styled from 'styled-components';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { Column } from '@tanstack/react-table';
import * as tokens from '@utmost.co/utmost-design-tokens';
import { Typography } from '../../Typography/Typography';
import { SystemIcon, SystemIconButton } from '../../Icon';
import { useDeepCompare } from '../../../hooks';
import { useDidMount } from '../../../hooks/useCompare';
import Drawer from '../../Drawer/Drawer';
import { UtilityColumns, expandedColumn, selectionColumn } from '../hooks/columnHooks';
import { useDataTableContext } from '../utils/DataTableContext';
import { useIntlUds } from '../../../translations/UdsIntlProvider';
import { getHeader } from '../utils/getNodeText';

const columnsConfigFilterRegex = new RegExp(['menu', selectionColumn.id, expandedColumn.id].join('|'));

const reorder = (list: Column<object, unknown>[], startIndex: number, endIndex: number) => {
  const result = [...list];
  const [removed] = result.splice(startIndex, 1);
  if (removed) {
    result.splice(endIndex, 0, removed);
  }

  return result;
};

type CustomElement = (props: { onClick: () => void }) => React.ReactNode;

export const ColumnsConfiguration: FC<React.PropsWithChildren<{ CustomElement?: CustomElement }>> = ({
  CustomElement
}) => {
  const { intl } = useIntlUds();
  const { getAllLeafColumns, setColumnOrder, onColumnConfigurationChanged, getState } = useDataTableContext();
  const state = getState?.();
  const allColumns = getAllLeafColumns?.();
  const [isColumnConfigOpen, setColumnConfigVisibility] = useState(false);
  const [allColumnsVisible, setAllColumnsVisible] = useState(true);
  const colVisibilityRef = useRef<HTMLButtonElement>(null);
  const columnsWithAvailableConfig = allColumns?.filter((column) => !columnsConfigFilterRegex.test(column.id));
  const areAllColsVisible = !!columnsWithAvailableConfig?.every((column) => column.getIsVisible());
  const columnVisibility = state?.columnVisibility;
  const columnOrder = state?.columnOrder;
  const isResizing = !!state?.columnSizingInfo.isResizingColumn;
  const columnSizing = state?.columnSizing;

  const onDrawerClose = () => setColumnConfigVisibility(false);
  const onDrawerOpen = () => setColumnConfigVisibility(true);
  const onDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    const reorderedColumns = reorder(columnsWithAvailableConfig ?? [], result.source.index, result.destination.index);
    setColumnOrder?.([
      UtilityColumns.EXPANDER,
      UtilityColumns.SELECTION,
      ...reorderedColumns.map((column) => column.id)
    ]);
  };

  const shouldUpdateConfigurationCallback =
    useDeepCompare({ columnVisibility, columnOrder, columnSizing, isResizing }) && !isResizing;

  useDidMount(() => {
    // Update only if configuration has changed and ignore first load
    if (shouldUpdateConfigurationCallback) {
      onColumnConfigurationChanged?.({
        hiddenColumns: Object.entries(columnVisibility ?? {})
          .filter(([, isVisible]) => !isVisible)
          .map(([key]) => key),
        columnOrder,
        columnSizing
      });
    }
  }, [onColumnConfigurationChanged, columnSizing, columnVisibility, columnOrder, shouldUpdateConfigurationCallback]);

  useEffect(() => {
    setAllColumnsVisible(areAllColsVisible);
  }, [areAllColsVisible]);
  const Wrapper = !!CustomElement ? React.Fragment : 'div';
  return (
    <Wrapper>
      {CustomElement ? (
        CustomElement({ onClick: onDrawerOpen })
      ) : (
        <SystemIconButton
          ref={colVisibilityRef}
          color={!allColumnsVisible ? 'active' : undefined}
          aria-label={intl.DataTable.ColumnsConfiguration.label}
          dataTest="col-visibility"
          className="col visibility"
          tooltipText={intl.DataTable.ColumnsConfiguration.label}
          iconName="view_column"
          onClick={onDrawerOpen}
        />
      )}
      <Drawer
        open={isColumnConfigOpen}
        anchor="right"
        aria-label={intl.DataTable.ColumnsConfiguration.label}
        dataTest="columns-configuration-drawer"
        variant="temporary"
        onClose={onDrawerClose}
      >
        <StyledPanel>
          <StyledHeader>
            <Typography variant="h3">{intl.DataTable.ColumnsConfiguration.label}</Typography>

            <SystemIconButton dataTest="close-column-configuration-drawer" iconName="close" onClick={onDrawerClose} />
            <StyledSubTitle>{intl.DataTable.ColumnsConfiguration.subTitle}</StyledSubTitle>
          </StyledHeader>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable-column-config">
              {(droppableProvided) => (
                <div {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
                  {columnsWithAvailableConfig?.map((column, index) => {
                    const isVisible = column.getIsVisible();
                    const blockColumnHiding = column.columnDef.meta?.blockColumnHiding;
                    return (
                      <Draggable key={column.id} draggableId={column.id} index={index}>
                        {(draggableProvided, draggableSnapshot) => (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            {...draggableProvided.dragHandleProps}
                            style={getItemStyle(draggableSnapshot.isDragging, draggableProvided.draggableProps.style)}
                          >
                            <StyledColumnConfiguration
                              key={column.id}
                              variant="body1"
                              dataTest="data-table-column-config"
                              data-config-enabled={isVisible}
                            >
                              <StyledSystemIcon className="drag-icon" name="drag_indicator" size="small" />
                              <span data-test="data-table-column-config-label">{getHeader(column)}</span>
                              <SystemIconButton
                                dataTest="data-table-toggle-column-visibility"
                                data-test-value={`${column.id}_${blockColumnHiding ? 'blocked' : 'unblocked'}`}
                                disabled={blockColumnHiding}
                                iconName={isVisible ? 'visibility' : 'visibility_off'}
                                size="small"
                                onClick={() => column.toggleVisibility(!isVisible)}
                              />
                            </StyledColumnConfiguration>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {droppableProvided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </StyledPanel>
      </Drawer>
    </Wrapper>
  );
};

const StyledPanel = styled.div`
  padding: ${({ theme }) => theme.spacingSize.xl};
  background: ${({ theme }) => theme.colorSurface};
  height: 100vh;
  max-width: 400px;
  min-width: 400px;
`;

const StyledHeader = styled.div`
  display: grid;
  width: 100%;
  gap: ${({ theme }) => theme.spacingSize.l};
  grid-template-columns: auto 48px;
  align-items: center;
  padding-top: ${({ theme }) => theme.spacingSize.m};
  padding-bottom: ${({ theme }) => theme.spacingSize.l};
`;

const getItemStyle = (isDragging: boolean, draggableStyle: CSSProperties | undefined): CSSProperties => ({
  background: isDragging ? `color-mix(in srgb, ${tokens.paletteNeutral20} 16%, transparent)` : 'none',
  // styles we need to apply on draggables
  ...draggableStyle
});

const StyledColumnConfiguration = styled(Typography)`
  display: grid;
  width: 100%;
  grid-template-columns: 42px auto 42px;
  align-items: center;
  border-radius: ${({ theme }) => theme.radiusDefault};
  .drag-icon {
    cursor: grab;
  }
  &:hover {
    background: ${({ theme }) => `color-mix(in srgb, ${theme.paletteNeutral20} 8%, transparent)`};
  }
`;

const StyledSubTitle = styled.div`
  grid-column: 1 / 2;
  grid-row: 2;
`;

const StyledSystemIcon = styled(SystemIcon)`
    margin-left: 12px;
`;
